import { ReactNode, useCallback, useMemo, useReducer } from 'react';
import { RouteData } from '@sitecore-jss/sitecore-jss-nextjs';

import { MajorLink, SCMajorLinkCard, SubmenuItem } from '../../megaMenuTypes';
import { HeaderContext } from './header.context';

export interface HeaderFields {
  items?: SubmenuItem[];
  cards?: Record<string, SCMajorLinkCard>;
}

export interface HeaderProviderProps {
  children?: ReactNode;
  fields?: HeaderFields;
  currentLocale?: string;
  mediaHost?: string;
  route: RouteData | undefined;
  onLanguageSelect?: () => void;
  myJabraUrl?: string;
}

interface HeaderState {
  activeItemId: string;
  isMobileMenuOpen: boolean;
  shouldFocus: boolean;
  activeMajorLink: MajorLink | null;
}

type HeaderAction =
  | { type: 'MENU_ITEM_CLICK'; payload: { name: string; isActive: boolean } }
  | { type: 'OPEN_MENU_ITEM'; payload: string }
  | { type: 'TOGGLE_MOBILE_MENU'; payload: { isOpen: boolean } }
  | { type: 'CLOSE_MENU' }
  | { type: 'BACK_CLICK'; payload: { hasActiveItem: boolean } }
  | { type: 'SET_MAJOR_CATEGORY'; payload: MajorLink };

const initialState: HeaderState = {
  activeItemId: '',
  isMobileMenuOpen: false,
  shouldFocus: false,
  activeMajorLink: null,
};

const headerReducer = (state: HeaderState, action: HeaderAction): HeaderState => {
  switch (action.type) {
    case 'MENU_ITEM_CLICK': {
      const { name, isActive } = action.payload;
      const shouldClose = isActive;
      return {
        ...state,
        activeItemId: shouldClose ? '' : name,
        isMobileMenuOpen: !shouldClose,
        shouldFocus: false,
        activeMajorLink: null,
      };
    }
    case 'OPEN_MENU_ITEM':
      return {
        ...state,
        activeItemId: action.payload,
        isMobileMenuOpen: true,
        shouldFocus: true,
        activeMajorLink: null,
      };
    case 'TOGGLE_MOBILE_MENU': {
      const { isOpen } = action.payload;
      return {
        ...state,
        activeItemId: isOpen ? '' : state.activeItemId,
        isMobileMenuOpen: !isOpen,
      };
    }
    case 'CLOSE_MENU':
      return {
        ...state,
        activeItemId: '',
        isMobileMenuOpen: false,
        shouldFocus: false,
      };
    case 'BACK_CLICK': {
      const { hasActiveItem } = action.payload;
      if (hasActiveItem) {
        return {
          ...state,
          activeItemId: '',
          shouldFocus: false,
        };
      }
      return {
        ...state,
        activeItemId: '',
        isMobileMenuOpen: false,
        shouldFocus: false,
      };
    }
    case 'SET_MAJOR_CATEGORY':
      return {
        ...state,
        activeMajorLink: action.payload,
      };
    default:
      return state;
  }
};

export const HeaderProvider = ({
  fields,
  children,
  currentLocale = 'en',
  route,
  mediaHost = '',
  onLanguageSelect,
  myJabraUrl = '',
}: HeaderProviderProps): React.JSX.Element => {
  const [state, dispatch] = useReducer(headerReducer, initialState);
  const { activeItemId, isMobileMenuOpen, shouldFocus, activeMajorLink } = state;

  const menuItems = useMemo(() => fields?.items ?? [], [fields?.items]);
  const cards = useMemo(() => fields?.cards, [fields?.cards]);

  const activeSubmenuItem = useMemo(
    () =>
      activeItemId && menuItems.length
        ? menuItems.find(
            (item: SubmenuItem) => item.name.toLowerCase() === activeItemId.toLowerCase()
          )
        : undefined,
    [activeItemId, menuItems]
  );

  const onMenuItemClick = useCallback(
    (menuItem: SubmenuItem) => {
      const name = menuItem.name;
      const isActive = activeItemId === name;
      dispatch({ type: 'MENU_ITEM_CLICK', payload: { name, isActive } });
    },
    [activeItemId]
  );

  const openMenuItem = useCallback((menuItem: SubmenuItem) => {
    dispatch({ type: 'OPEN_MENU_ITEM', payload: menuItem.name });
  }, []);

  const onMobileMenuClick = useCallback(() => {
    dispatch({ type: 'TOGGLE_MOBILE_MENU', payload: { isOpen: isMobileMenuOpen } });
  }, [isMobileMenuOpen]);

  const closeMenu = useCallback(() => {
    dispatch({ type: 'CLOSE_MENU' });
  }, []);

  const onBackClick = useCallback(() => {
    dispatch({ type: 'BACK_CLICK', payload: { hasActiveItem: !!activeItemId } });
  }, [activeItemId]);

  const onMajorCategoryHover = useCallback((majorLink: MajorLink) => {
    dispatch({ type: 'SET_MAJOR_CATEGORY', payload: majorLink });
  }, []);

  return (
    <HeaderContext.Provider
      value={useMemo(
        () => ({
          activeItemId,
          isMobileMenuOpen,
          menuItems,
          shouldFocus,
          onMenuItemClick,
          onMobileMenuClick,
          activeSubmenuItem,
          onBackClick,
          activeMajorLink,
          onMajorCategoryHover,
          closeMenu,
          openMenuItem,
          onLanguageSelect,
          currentLocale,
          mediaHost,
          myJabraUrl,
          route,
          cards,
        }),
        [
          activeItemId,
          isMobileMenuOpen,
          menuItems,
          shouldFocus,
          onMenuItemClick,
          onMobileMenuClick,
          activeSubmenuItem,
          onBackClick,
          activeMajorLink,
          onMajorCategoryHover,
          closeMenu,
          openMenuItem,
          onLanguageSelect,
          currentLocale,
          mediaHost,
          myJabraUrl,
          route,
          cards,
        ]
      )}
    >
      {children}
    </HeaderContext.Provider>
  );
};
